'use client';

import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { PICKLEBALL_PLACEHOLDER_IMAGE } from '@pickleballinc/ui/components/global/utils/constants';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import { ImageSlider } from '@pickleballinc/ui/components/ui/image-slider';
import { Button } from '@pickleballinc/uiv2/components/button';
import clsx from 'clsx';
import type { CSSProperties, ReactNode } from 'react';

import { HeartIcon } from './HeartIcon';

export interface ModuleCardProps {
	id?: string;
	images: string[];
	imageAlt: string;
	isPlaceholderImage?: boolean;
	href?: string;
	target?: '_blank' | '_self';
	rel?: string;
	/**
	 * Render the click-through as a plain `<a>` (full document navigation) instead of a Next `<Link>`.
	 * Set by the listing-ad cards: an ad's URL carries Everflow parameters that the landing middleware
	 * has to see, and Next would route a same-origin URL client-side and skip it.
	 */
	nativeAnchor?: boolean;
	topBanner?: ReactNode;
	topLeftSlot?: ReactNode;
	showFavoriteIcon?: boolean;
	isUserLoggedIn?: boolean;
	isFavourite?: boolean | null;
	isFavoriteDisabled?: boolean;
	onFavoriteToggle?: (event: React.MouseEvent, id?: string, isFavourite?: boolean | null) => void;
	onClose?: () => void;
	isMapView?: boolean;
	className?: string;
	bodyClassName?: string;
	imageClassName?: string;
	imageContainerClassName?: string;
	imageContainerStyle?: CSSProperties;
	children: ReactNode;
	smallCard?: boolean;
	backgroundColor?: string;
	backgroundHoverColor?: string;
	isPartnerCard?: boolean;
}

interface ModuleCardBodyStyle extends CSSProperties {
	'--module-card-bg'?: string;
	'--module-card-hover-bg'?: string;
}

export const ModuleCard = ({
	id,
	images,
	imageAlt,
	isPlaceholderImage = false,
	href,
	target,
	rel = 'noopener noreferrer',
	nativeAnchor = false,
	topBanner,
	topLeftSlot,
	showFavoriteIcon = true,
	isUserLoggedIn = false,
	isFavourite,
	isFavoriteDisabled,
	onFavoriteToggle,
	onClose,
	isMapView = false,
	className,
	bodyClassName,
	imageClassName,
	imageContainerClassName,
	smallCard = false,
	backgroundColor,
	backgroundHoverColor,
	isPartnerCard = false,
	imageContainerStyle,
	children
}: ModuleCardProps) => {
	const hasMultipleImages = images.length > 1;
	const hasBodyBackground = Boolean(backgroundColor);
	const placeholderImage = getCDNURL(PICKLEBALL_PLACEHOLDER_IMAGE, 566, 384);

	const bodyStyle: ModuleCardBodyStyle | undefined = hasBodyBackground
		? {
				'--module-card-bg': backgroundColor,
				'--module-card-hover-bg': backgroundHoverColor ?? backgroundColor
			}
		: undefined;

	const favoriteButton =
		showFavoriteIcon && isUserLoggedIn ? (
			<Button
				variant="ghost"
				size="xs"
				disabled={isFavoriteDisabled}
				onClick={(event) => onFavoriteToggle?.(event, id, isFavourite)}
				className={clsx(
					'shrink-0 transition-transform duration-150',
					!isFavoriteDisabled && 'hover:scale-110 active:scale-95',
					isFavoriteDisabled && 'cursor-not-allowed',
					isMapView && 'rounded-full border border-gray-300 bg-white shadow-md hover:bg-white'
				)}
				style={{
					height: isMapView ? 36 : 26,
					width: isMapView ? 36 : 26,
					minWidth: isMapView ? 36 : 26,
					flexShrink: 0,
					padding: 0,
					opacity: isFavoriteDisabled ? 0.8 : 1
				}}
			>
				<span className="relative inline-flex items-center justify-center" style={{ height: 20, width: 20 }}>
					<HeartIcon isFavourite={isFavourite} />
				</span>
			</Button>
		) : null;

	const closeButton =
		isMapView && onClose ? (
			<Button
				variant="ghost"
				size="xs"
				onClick={(event) => {
					event.preventDefault();
					event.stopPropagation();
					onClose();
				}}
				aria-label="Close map card"
				className="size-9 min-w-9 shrink-0 rounded-full border border-gray-300 bg-white p-0 shadow-md transition-transform duration-150 hover:scale-110 hover:bg-white active:scale-95"
			>
				<span className="relative inline-flex size-5 items-center justify-center">
					<FontAwesomeIcon icon={faXmark} className="size-5 text-gray-900" />
				</span>
			</Button>
		) : null;

	const hasTopRightControls = (isUserLoggedIn && favoriteButton) || closeButton;

	const imageSection = (
		<div
			className={clsx(
				imageContainerClassName,
				'relative flex aspect-video w-full self-stretch items-start gap-2.5 overflow-hidden border border-[#E9EAEB] hover:border-[#A4A7AE] group-hover:border-[#A4A7AE] bg-white',
				hasBodyBackground ? 'rounded-t-2xl' : 'rounded-2xl',
				topLeftSlot ? 'justify-between' : 'justify-end'
			)}
			style={imageContainerStyle}
		>
			<div className="h-full w-full">
				{hasMultipleImages && href ? (
					<ImageSlider
						customHref={href}
						target={target}
						rel={target === '_blank' ? rel : undefined}
						size="sm"
						containerClassNames="h-full"
						images={images}
						isPlaceholderImage={isPlaceholderImage}
						isCardVariant
						imageClassName={imageClassName}
						objectCover
					/>
				) : (
					<img
						src={images[0] ?? placeholderImage}
						alt={imageAlt}
						className={clsx('h-full w-full', isPlaceholderImage && 'p-4', imageClassName)}
					/>
				)}
			</div>

			{topLeftSlot}
			{hasTopRightControls ? (
				<div className={clsx('absolute right-2 top-2 flex items-center gap-2', isMapView && 'z-30')}>
					{isUserLoggedIn ? favoriteButton : null}
					{closeButton}
				</div>
			) : null}
		</div>
	);

	const bodySection = (
		<div
			className={clsx(
				'relative z-20 flex flex-1 flex-col self-stretch justify-between gap-2 rounded-b-xl transition-colors duration-150',
				hasBodyBackground ? 'px-4 py-3' : 'px-4',
				hasBodyBackground &&
					(isPartnerCard
						? 'bg-[color-mix(in_srgb,var(--module-card-bg)_60%,transparent)] hover:bg-[color-mix(in_srgb,var(--module-card-hover-bg)_60%,transparent)]'
						: 'bg-[var(--module-card-bg)] hover:bg-[var(--module-card-hover-bg)]'),
				hasBodyBackground && isPartnerCard && 'border border-transparent hover:border-[var(--module-card-hover-bg)]',
				isMapView && !hasBodyBackground && 'bg-white',
				bodyClassName
			)}
			style={bodyStyle}
		>
			{children}
		</div>
	);

	const outerClassName = clsx(
		className,
		'group relative flex cursor-pointer flex-col items-center overflow-hidden rounded-xl',
		hasBodyBackground ? 'gap-0 pb-0' : 'gap-2.5 pb-4',
		'w-full min-w-0 max-w-[343px]',
		!smallCard && 'min-[500px]:w-auto min-[500px]:min-w-[224px] min-[500px]:max-w-[286px]',
		smallCard && 'min-[500px]:min-w-[215px] min-[500px]:max-w-[215px]',
		isMapView && 'bg-white'
	);

	if (hasMultipleImages) {
		return (
			<div className={outerClassName}>
				{topBanner}
				{imageSection}
				{href ? (
					target === '_blank' ? (
						<a href={href} target={target} rel={rel} className="block self-stretch">
							{bodySection}
						</a>
					) : (
						<CustomLink href={href} target={target} className="block self-stretch" native={nativeAnchor}>
							{bodySection}
						</CustomLink>
					)
				) : (
					bodySection
				)}
			</div>
		);
	}

	const content = (
		<>
			{topBanner}
			{imageSection}
			{bodySection}
		</>
	);

	if (!href) {
		return <div className={outerClassName}>{content}</div>;
	}

	return target === '_blank' ? (
		<a href={href} target={target} rel={rel} className="block self-stretch">
			<div className={outerClassName}>{content}</div>
		</a>
	) : (
		<CustomLink href={href} className="block self-stretch" native={nativeAnchor}>
			<div className={outerClassName}>{content}</div>
		</CustomLink>
	);
};
